import { motion } from "framer-motion";
import { Users, Settings2, Zap } from "lucide-react";
import GlowEffect from "../common/GlowEffect";
import { useAIAgentData } from "../../hooks/useAIAgentData";

// Icon mapping dictionary to resolve string names to Lucide icon components
const iconMap: Record<string, React.ComponentType<any>> = {
  Users,
  Settings2,
  Zap,
};

function renderIcon(name: string, size: number, className?: string) {
  const Icon = iconMap[name];
  return Icon ? <Icon size={size} className={className} /> : null;
}

// Shared animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12 },
  }),
} as const;

export default function HowItWorksSection() {
  const { data } = useAIAgentData();
  const { badgeText, title, steps, footerText } = data.howItWorksSection;

  return (
    <section className="relative px-5 sm:px-10 md:px-16 sm:py-16 py-8">
      <GlowEffect direction="right" />
      <div className="space-y-16">
        <div className="text-center space-y-4">
          <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-[#A6A0F6] text-sm font-medium tracking-widest uppercase">
            {badgeText}
          </motion.p>
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="text-4xl md:text-6xl font-bold text-white">
            {title}
          </motion.h2>
        </div>

        <div className="relative grid md:grid-cols-3 gap-12 pt-8">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-10 left-[calc(16.67%+2rem)] right-[calc(16.67%+2rem)] h-px bg-gradient-to-r from-transparent via-[#1179F9]/60 to-transparent" />

          {steps.map((step, i) => (
            <motion.div
              key={i}
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
              className="flex flex-col items-center text-center space-y-5"
            >
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-b from-[#3982fd] to-[#bc98ff] flex items-center justify-center text-white border border-[#cb97ff] z-10">
                {renderIcon(step.iconName, 28)}
                <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white text-[#1179F9] flex items-center justify-center text-xs font-bold">{i + 1}</span>
              </div>
              <h3 className="text-2xl lg:text-3xl font-semibold text-white">{step.title}</h3>
              <p className="text-2xl text-[#d9d9d9] leading-relaxed max-w-sm">{step.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center text-[#d9d9d9] text-xl">
          {footerText}
        </motion.p>
      </div>
    </section>
  );
}
